import { Project, AgentResult, AutopilotRule, Company, Task } from '../types';
import { runAgent } from './agentRegistry';
import { EmailAgentService } from './emailAgent';
import { MemoryService } from './memoryService';

export class AutopilotEngine {

    static async processResult(project: Project, result: AgentResult, onUpdateProject: (p: Project) => void, onRunAgent: (id: string, options: any) => Promise<any>) {
        if (!project.autopilot || !project.autopilot.isEnabled) {
            console.log("Autopilot disabled for project");
            return;
        }

        console.log(`[Autopilot] Processing result from ${result.agentId}`);
        const rules = project.autopilot.rules.filter(r => r.sourceAgentId === result.agentId && r.enabled);

        if (rules.length === 0) {
            console.log("[Autopilot] No rules found for this agent.");
            return;
        }

        for (const rule of rules) {
            await this.executeRule(project, rule, result, onRunAgent);
        }
    }

    private static async executeRule(project: Project, rule: AutopilotRule, result: AgentResult, onRunAgent: (id: string, options: any) => Promise<any>) {
        console.log(`[Autopilot] Executing Rule: ${rule.sourceAgentId} -> ${rule.targetAgentId}`);

        // CASE 1: Lead Finder -> Researcher
        if (rule.sourceAgentId === 'lead-finder' && rule.targetAgentId === 'researcher') {
            const leads = result.data || [];
            if (!Array.isArray(leads)) return;

            // Trigger Research for each lead concurrently to reduce latency
            const researchPromises = leads.map(async (lead) => {
                // Condition Check
                if (rule.condition === 'has_website' && !lead.website) return;
                
                console.log(`[Autopilot] Triggering Research for ${lead.companyName}`);
                
                // Trigger the next step so the UI updates
                return onRunAgent('researcher', {
                    companyName: lead.companyName,
                    companyId: lead.companyId,
                    mode: 'deep'
                });
            });

            // Wait for all research agents to finish
            await Promise.all(researchPromises);
        }

        // CASE 2: Researcher -> Email Drafter
        else if (rule.sourceAgentId === 'researcher' && rule.targetAgentId === 'email-outreach') {
             const data = result.data;
             // Check if we have valid contact info
             const hasEmail = data.contactInfo?.email;
             
             if (rule.condition === 'always' || (rule.condition === 'has_website' && hasEmail)) {
                 console.log(`[Autopilot] Drafting Email for ${data.companyName}`);
                 
                 // Draft the email
                 const draft = await EmailAgentService.draftEmail(project, { 
                     id: data.companyId || 'temp', 
                     name: data.companyName, 
                     industry: data.industry || 'Unknown',
                     description: data.summary || '',
                     website: data.contactInfo?.website || '',
                     location: data.contactInfo?.address || '',
                     email: data.contactInfo?.email
                 }, 'initial');

                 // Save as a Task to review
                 MemoryService.addTask({
                     id: crypto.randomUUID(),
                     projectId: project.id,
                     companyId: data.companyId,
                     title: `Review Draft: ${draft.subject}`,
                     dueDate: new Date().toISOString(),
                     type: 'email',
                     status: 'pending'
                 });
             }
        }
    }
    
    // Helper to generate default rules for a new project
    static getDefaultRules(): AutopilotRule[] {
        return [
            {
                id: 'rule-1',
                sourceAgentId: 'lead-finder',
                targetAgentId: 'researcher',
                condition: 'always',
                enabled: false // Default off for safety
            },
            {
                id: 'rule-2',
                sourceAgentId: 'researcher',
                targetAgentId: 'email-outreach',
                condition: 'has_website', // Only if we found contact info
                enabled: false
            }
        ];
    }
}